import { isEmpty, sum } from 'lodash';
import React from 'react';

import {
    AccountConstraint,
    CashAllocation,
    ConvictionAllocation,
    ExposureStatus,
    GrowthAllocation,
    type SecuredAllocation,
} from '@/cip/_openapi/portfolio-optimizer';
import { toAccountConstraintFields } from '@/cip/Allocation/accountConstraintFields';
import { Badge } from '@/components/ui/badge';
import { Table, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TableSkeletonRow } from '@/components/ui/table-skeleton-row';
import { formatCurrency, formatNumber, formatPercent, getAllocationTypeLabel, titleCase } from 'utils';

const exposureTypeMap: Record<string, string> = {
    fee: 'Fee',
    buffer: 'Buffer',
    ad_hoc_withdrawal: 'Ad Hoc Withdrawal',
    planned_withdrawal: 'Planned Withdrawal',
    unencumbered: 'Unencumbered',
    is_payday_bond: 'Payday Bond',
};

export const ExposureStatusBadge = ({ value }: { value: ExposureStatus | undefined }) => {
    let className;
    switch (value) {
        case ExposureStatus.OVERWEIGHT:
            className = 'bg-red-200 text-red-800 hover:red-100';
            break;
        case ExposureStatus.UNDERWEIGHT:
            className = 'bg-blue-200 text-blue-800 hover:blue-100';
            break;
        case ExposureStatus.ON_TARGET:
            className = 'bg-green-200 text-green-800 hover:green-100';
            break;
        default:
            className = '';
            break;
    }

    return (
        <Badge variant="secondary" className={className}>
            {titleCase(value)}
        </Badge>
    );
};

const EmptyTableContent = ({ colSpan, entity }: { colSpan: number; entity: string }) => {
    return (
        <TableRow>
            <TableCell colSpan={colSpan} className="8 text-center">
                <p className="text-muted-foreground">No {entity} found</p>
            </TableCell>
        </TableRow>
    );
};

type ExposureAllocation = {
    currentExposure?: number;
    targetExposure?: number;
    activeExposure?: number;
};

const ExposureTotalFooter = ({
    allocations,
    trailingColumns,
}: {
    allocations?: Array<ExposureAllocation>;
    trailingColumns: number;
}) => {
    if (isEmpty(allocations)) {
        return null;
    }

    const currentExposure = sum(allocations?.map((allocation) => allocation.currentExposure ?? 0));
    const targetExposure = sum(allocations?.map((allocation) => allocation.targetExposure ?? 0));
    const activeExposure = sum(allocations?.map((allocation) => allocation.activeExposure ?? 0));

    return (
        <TableFooter>
            <TableRow className="hover:bg-transparent">
                <TableCell className="py-1">Total</TableCell>
                <TableCell className="py-1 whitespace-nowrap">{formatCurrency(currentExposure)}</TableCell>
                <TableCell className="py-1 whitespace-nowrap">{formatCurrency(targetExposure)}</TableCell>
                <TableCell className="py-1 whitespace-nowrap">{formatCurrency(activeExposure)}</TableCell>
                {Array.from({ length: trailingColumns }).map((_, i) => (
                    <TableCell key={`total-empty-${i}`} className="py-1" />
                ))}
            </TableRow>
        </TableFooter>
    );
};

export const AccountConstraintRow = ({ accountConstraint }: { accountConstraint?: AccountConstraint | null }) => {
    const fields = toAccountConstraintFields(accountConstraint);
    if (fields.length === 0) {
        return null;
    }
    return (
        <div className="flex gap-8 px-2 pb-2 text-sm whitespace-nowrap">
            {fields.map((field) => (
                <span key={field.label}>
                    <span className="text-gray-500">{field.label}</span> {field.value}
                    {field.isCapped && (
                        <Badge variant="secondary" className="ml-1 bg-orange-200 text-orange-800">
                            capped
                        </Badge>
                    )}
                </span>
            ))}
        </div>
    );
};

export const GrowthAllocationTable = ({
    allocations,
    accountConstraint,
    isLoading,
}: {
    allocations?: Array<GrowthAllocation>;
    accountConstraint?: AccountConstraint | null;
    isLoading: boolean;
}) => {
    return (
        <div className="">
            <AccountConstraintRow accountConstraint={accountConstraint} />
            <Table>
                <TableHeader>
                    <TableRow className="hover:bg-transparent">
                        <TableHead className="w-48 font-medium text-gray-700">Allocation Type</TableHead>
                        <TableHead className="font-medium text-gray-700">Current exposure</TableHead>
                        <TableHead className="font-medium text-gray-700">Target exposure</TableHead>
                        <TableHead className="font-medium text-gray-700">Active exposure</TableHead>
                        <TableHead className="font-medium text-gray-700">Exposure status</TableHead>
                        <TableHead className="font-medium text-gray-700">Current weights</TableHead>
                        <TableHead className="font-medium text-gray-700">Target weight</TableHead>
                        <TableHead className="font-medium text-gray-700">Active weight</TableHead>
                    </TableRow>
                </TableHeader>
                <tbody>
                    {!isLoading && isEmpty(allocations) && (
                        <EmptyTableContent colSpan={8} entity="Growth Allocations" />
                    )}
                    {isLoading
                        ? [1, 2, 3].map((i) => <TableSkeletonRow key={`loading-row-${i}`} columns={8} />)
                        : allocations?.map((allocation, i) => (
                              <TableRow key={`growth-allocation-${i}`} className="p-2 hover:bg-gray-50">
                                  <TableCell className="w-48 py-1">
                                      {getAllocationTypeLabel(allocation.allocationType)}
                                  </TableCell>
                                  <TableCell className="py-1 whitespace-nowrap">
                                      {formatCurrency(allocation.currentExposure)}
                                  </TableCell>
                                  <TableCell className="py-1 whitespace-nowrap">
                                      {formatCurrency(allocation.targetExposure)}
                                  </TableCell>
                                  <TableCell className="py-1 whitespace-nowrap">
                                      {formatCurrency(allocation.activeExposure)}
                                  </TableCell>
                                  <TableCell className="py-1">
                                      <div className="flex items-center justify-center">
                                          <ExposureStatusBadge value={allocation.exposureStatus} />
                                      </div>
                                  </TableCell>
                                  <TableCell className="py-1">
                                      {allocation.currentWeight != null
                                          ? formatPercent(allocation.currentWeight * 100, 1)
                                          : ''}
                                  </TableCell>
                                  <TableCell className="py-1">
                                      {allocation.targetWeight != null
                                          ? formatPercent(allocation.targetWeight * 100, 1)
                                          : ''}
                                  </TableCell>
                                  <TableCell className="py-1">
                                      {allocation.activeWeight != null
                                          ? formatPercent(allocation.activeWeight * 100, 1)
                                          : ''}
                                  </TableCell>
                              </TableRow>
                          ))}
                </tbody>
                {!isLoading && <ExposureTotalFooter allocations={allocations} trailingColumns={4} />}
            </Table>
        </div>
    );
};

export const CashAllocationTable = ({
    allocations,
    isLoading,
}: {
    allocations?: Array<CashAllocation>;
    isLoading: boolean;
}) => {
    return (
        <div className="">
            <Table>
                <TableHeader>
                    <TableRow className="hover:bg-transparent">
                        <TableHead className="font-medium text-gray-700">Exposure type</TableHead>
                        <TableHead className="font-medium text-gray-700">Current exposure</TableHead>
                        <TableHead className="font-medium text-gray-700">Target exposure</TableHead>
                        <TableHead className="font-medium text-gray-700">Active exposure</TableHead>
                    </TableRow>
                </TableHeader>
                <tbody>
                    {!isLoading && isEmpty(allocations) && <EmptyTableContent colSpan={4} entity="Cash Allocations" />}
                    {isLoading
                        ? [1, 2, 3].map((i) => <TableSkeletonRow key={`loading-row-${i}`} columns={4} />)
                        : allocations?.map((allocation, i) => (
                              <TableRow key={`cash-allocation-${i}`} className="hover:bg-gray-50">
                                  <TableCell className="py-1">
                                      {exposureTypeMap[allocation.exposureType] || titleCase(allocation.exposureType)}
                                  </TableCell>
                                  <TableCell className="py-1 whitespace-nowrap">
                                      {formatCurrency(allocation.currentExposure)}
                                  </TableCell>
                                  <TableCell className="py-1 whitespace-nowrap">
                                      {formatCurrency(allocation.targetExposure)}
                                  </TableCell>
                                  <TableCell className="py-1 whitespace-nowrap">
                                      {formatCurrency(allocation.activeExposure)}
                                  </TableCell>
                              </TableRow>
                          ))}
                </tbody>
                {!isLoading && <ExposureTotalFooter allocations={allocations} trailingColumns={0} />}
            </Table>
        </div>
    );
};

export const SecuredAllocationTable = ({
    allocations,
    isLoading,
}: {
    allocations?: Array<SecuredAllocation>;
    isLoading: boolean;
}) => {
    return (
        <div className="">
            <Table>
                <TableHeader>
                    <TableRow className="hover:bg-transparent">
                        <TableHead className="w-32 font-medium text-gray-700">Exposure type</TableHead>
                        <TableHead className="font-medium text-gray-700">Current exposure</TableHead>
                        <TableHead className="font-medium text-gray-700">Target exposure</TableHead>
                        <TableHead className="font-medium text-gray-700">Active exposure</TableHead>
                        <TableHead className="font-medium text-gray-700">Exposure status</TableHead>
                        <TableHead className="font-medium text-gray-700">Target Securedness</TableHead>
                        <TableHead className="font-medium text-gray-700">Previous Target Securedness</TableHead>
                        <TableHead className="font-medium text-gray-700">Initial Securedness</TableHead>
                        <TableHead className="font-medium text-gray-700">Target Duration</TableHead>
                        <TableHead className="font-medium text-gray-700">Initial Duration</TableHead>
                        <TableHead className="font-medium text-gray-700">Target Fundedness</TableHead>
                        <TableHead className="font-medium text-gray-700">Initial Fundedness</TableHead>
                    </TableRow>
                </TableHeader>
                <tbody>
                    {!isLoading && isEmpty(allocations) && (
                        <EmptyTableContent colSpan={12} entity="Secured Allocations" />
                    )}
                    {isLoading
                        ? [1, 2, 3].map((i) => <TableSkeletonRow key={`loading-row-${i}`} columns={12} />)
                        : allocations?.map((allocation, i) => (
                              <TableRow key={`secured-allocation-${i}`} className="h-2 hover:bg-gray-50">
                                  <TableCell className="py-1">
                                      {exposureTypeMap[allocation.exposureType] || titleCase(allocation.exposureType)}
                                  </TableCell>
                                  <TableCell className="py-1 whitespace-nowrap">
                                      {formatCurrency(allocation.currentExposure)}
                                  </TableCell>
                                  <TableCell className="py-1 whitespace-nowrap">
                                      {formatCurrency(allocation.targetExposure)}
                                  </TableCell>
                                  <TableCell className="py-1 whitespace-nowrap">
                                      {formatCurrency(allocation.activeExposure)}
                                  </TableCell>
                                  <TableCell className="py-1">
                                      <ExposureStatusBadge value={allocation.exposureStatus} />
                                  </TableCell>
                                  <TableCell className="py-1">
                                      {allocation.targetSecuredness != null
                                          ? formatPercent(allocation.targetSecuredness * 100, 1)
                                          : ''}
                                  </TableCell>
                                  <TableCell className="py-1">
                                      {allocation.previousTargetSecuredness != null
                                          ? formatPercent(allocation.previousTargetSecuredness * 100, 1)
                                          : ''}
                                  </TableCell>
                                  <TableCell className="py-1">
                                      {allocation.initialSecuredness != null
                                          ? formatPercent(allocation.initialSecuredness * 100, 1)
                                          : ''}
                                  </TableCell>
                                  <TableCell className="py-1">
                                      {allocation.targetDuration != null
                                          ? formatNumber(allocation.targetDuration, 2)
                                          : ''}
                                  </TableCell>
                                  <TableCell className="py-1">
                                      {allocation.initialDuration != null
                                          ? formatNumber(allocation.initialDuration, 2)
                                          : ''}
                                  </TableCell>
                                  <TableCell className="py-1">
                                      {allocation.targetFundedness != null
                                          ? formatPercent(allocation.targetFundedness * 100, 1)
                                          : ''}
                                  </TableCell>
                                  <TableCell className="py-1">
                                      {allocation.initialFundedness != null
                                          ? formatPercent(allocation.initialFundedness * 100, 1)
                                          : ''}
                                  </TableCell>
                              </TableRow>
                          ))}
                </tbody>
                {!isLoading && <ExposureTotalFooter allocations={allocations} trailingColumns={8} />}
            </Table>
        </div>
    );
};

export const ConvictionAllocationTable = ({
    allocations,
    isLoading,
}: {
    allocations?: Array<ConvictionAllocation>;
    isLoading: boolean;
}) => {
    return (
        <div className="">
            <Table>
                <TableHeader>
                    <TableRow className="hover:bg-transparent">
                        <TableHead className="w-48 font-medium text-gray-700">Allocation type</TableHead>
                        <TableHead className="font-medium text-gray-700">Current exposure</TableHead>
                        <TableHead className="font-medium text-gray-700">Target exposure</TableHead>
                        <TableHead className="font-medium text-gray-700">Active exposure</TableHead>
                        <TableHead className="font-medium text-gray-700">Max target exposure</TableHead>
                    </TableRow>
                </TableHeader>
                <tbody>
                    {!isLoading && isEmpty(allocations) && (
                        <EmptyTableContent colSpan={5} entity="Conviction Allocations" />
                    )}
                    {isLoading
                        ? [1, 2, 3].map((i) => <TableSkeletonRow key={`loading-row-${i}`} columns={5} />)
                        : allocations?.map((allocation, i) => (
                              <TableRow key={`conviction-allocation-${i}`} className="hover:bg-gray-50">
                                  <TableCell>{getAllocationTypeLabel(allocation.allocationType)}</TableCell>
                                  <TableCell className="whitespace-nowrap">
                                      {formatCurrency(allocation.currentExposure)}
                                  </TableCell>
                                  <TableCell className="whitespace-nowrap">
                                      {formatCurrency(allocation.targetExposure)}
                                  </TableCell>
                                  <TableCell className="whitespace-nowrap">
                                      {formatCurrency(allocation.activeExposure)}
                                  </TableCell>
                                  <TableCell className="whitespace-nowrap">
                                      {formatCurrency(allocation.maxTargetExposure)}
                                  </TableCell>
                              </TableRow>
                          ))}
                </tbody>
                {!isLoading && <ExposureTotalFooter allocations={allocations} trailingColumns={1} />}
            </Table>
        </div>
    );
};
